import { Session } from 'next-auth';

import {
    getProductSpecifications,
    getProductsHighlights,
    getProductsManufacturerComparator,
    getProductsSpecialOffers,
} from '@services/cms/products';
import { httpService } from '@services/http';
import { HttpConfig, HttpService } from '@services/http/http';
import { reviewsServices } from '@services/reviews';

import { getServerSession } from '@actions/get-session';

import { mCubeRepository } from '@repositories/mcube';
import { sapiRepository } from '@repositories/sapi';

import { SapiProductChildType } from '@app-types/api/sapi';
import { WallType } from '@app-types/config';
import { ContractType } from '@app-types/contract';
import { MobilePlanType } from '@app-types/plan';
import { ProductParentFull, ProductStockDetails } from '@app-types/product';
import { DeliveryContextType } from '@app-types/product-delivery';
import { UserType } from '@app-types/user';

import { isServer } from '@helpers/config';
import { localApiPaths } from '@helpers/path';
import { addRenewalInfoToPlans } from '@helpers/product';

import { PlanTypeId } from '@constants/plan';

import { getChildStock, sapiProductMapper } from '@product-detail/helpers';

import { getMobilePlans } from './plan';
import { getApiProductDetails, mergeSapicToSapiProduct } from './products';

class ProductDetailsService {
    private httpService: HttpService;

    public constructor(httpService: HttpService) {
        this.httpService = httpService;
    }

    public async getProductDetails(
        type: WallType,
        name: string,
        isRenewal: boolean = false,
        shouldBeProspect: boolean = false,
        _session?: Session,
    ): Promise<ProductParentFull> {
        const session = shouldBeProspect ? null : (_session ?? (await getServerSession()));
        if (isServer()) {
            return this.getProductDetailsServer(type, name, isRenewal, session);
        }

        const config: HttpConfig = {};

        if (session?.user) {
            config.user = session.user as UserType;
        }

        return this.httpService.get<ProductParentFull>(
            localApiPaths.product,
            {
                type,
                name,
                isRenewal,
                shouldBeProspect,
            },
            config,
        );
    }

    public async getProductStock(gencode: string, deliveryContext?: DeliveryContextType): Promise<ProductStockDetails> {
        if (isServer()) {
            return this.getProductStockServer(gencode, deliveryContext);
        }

        const config: HttpConfig = {};

        if (deliveryContext?.user) {
            config.user = deliveryContext.user;
        }

        // Côté client, seul le contrat est transmis : l'identité de l'acheteur et son adresse sont
        // résolues côté serveur dans la route à partir de la session.
        return this.httpService.get<ProductStockDetails>(
            localApiPaths.productStock,
            {
                gencode,
                ...(deliveryContext?.contractId && { contractId: deliveryContext.contractId }),
            },
            config,
        );
    }

    public async getPlans(
        productGencode: string,
        isRenewal: boolean,
        planType: PlanTypeId,
        contract?: ContractType,
        session?: Session,
    ): Promise<MobilePlanType[]> {
        let plans: MobilePlanType[] = [];

        if (isRenewal && contract?.id && session?.user) {
            const galliumData = await mCubeRepository.getSimulationImpactsOffres(session.user as UserType, contract.id);

            if (!galliumData) {
                return [];
            }

            const plansSimulation = galliumData.simulations.map((offer) => offer.offerId);
            const plansGencode = [galliumData.sourceOffer.offerId, ...plansSimulation].filter((o) => o !== undefined);

            plans = await getMobilePlans({
                productGencode,
                isRenewal: true,
                categorie: planType,
                contractId: contract?.id,
                planGencodes: plansGencode,
                session,
            });

            plans = addRenewalInfoToPlans(plans, galliumData);
        } else {
            plans = await getMobilePlans({
                productGencode,
                isRenewal,
                categorie: planType,
                contractId: contract?.id,
                session,
                isClientBbox: !session,
            });
        }

        plans = plans.filter((p) => p.simulatedCart?.fundings.length);

        if (plans.length === 0) {
            return [];
        }

        return plans;
    }

    private async getProductDetailsServer(
        type: WallType,
        name: string,
        isRenewal: boolean,
        session: Session | null,
    ): Promise<ProductParentFull> {
        const productSapi = await getApiProductDetails(name);
        const isPhoneProduct = type === WallType.PHONE || type === WallType.SALES_PHONE;
        const [product, specifications, highlights, specialOffers, manufacturerComparator, reviews] = await Promise.all(
            [
                mergeSapicToSapiProduct(productSapi, session),
                getProductSpecifications(productSapi.url_key),
                getProductsHighlights(productSapi.url_key, isPhoneProduct),
                getProductsSpecialOffers(productSapi.url_key, isRenewal),
                isPhoneProduct && productSapi?.manufacturer
                    ? getProductsManufacturerComparator(productSapi.manufacturer)
                    : Promise.resolve(undefined),
                reviewsServices.getReviews(productSapi.gencode),
            ],
        );

        const productInfo = sapiProductMapper(product);

        return {
            ...productInfo,
            reviews,
            specificationsCms: specifications,
            highlightsCms: highlights,
            specialOffersCms: specialOffers,
            productComparatorCms: manufacturerComparator,
        };
    }

    private async getProductStockServer(
        gencode: string,
        deliveryContext?: DeliveryContextType,
    ): Promise<ProductStockDetails> {
        const product = await sapiRepository.getProductByGencode<SapiProductChildType>(gencode);
        return getChildStock(product, deliveryContext);
    }
}

export const productDetailsService = new ProductDetailsService(httpService);
